import React from "react";
import { Link } from "react-router-dom";
import { Lock, Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription, useHasModule } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Wrap any page section that requires a paid plan. Renders an overlay banner
 * over a *blurred* preview of the children when the module is not active.
 *
 *   <ModuleGate module="employees.payslip">
 *     <PayslipDashboard />
 *   </ModuleGate>
 *
 * Roles:
 *   - super_admin / sub_super_admin → no gating (always show children)
 *   - school_owner / sub_owner       → banner with "Upgrade" CTA → /billing
 *   - branch_admin / staff           → banner asking them to contact the owner
 */
export default function ModuleGate({ module: moduleKey, children, title, description }) {
  const { loading, expired } = useSubscription();
  const allowed = useHasModule(moduleKey);
  const { user } = useAuth();

  // Loading → render nothing (avoid mounting heavy children that might be hidden).
  if (loading) return null;
  // Allowed → just render children
  if (allowed) return children;

  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const moduleLabel = title || moduleKey;

  return (
    <div className="relative min-h-[60vh]" data-testid={`module-gate-${moduleKey}`}>
      {/* Lightweight placeholder behind the banner — keeps the layout
          stable but never mounts heavy children (Recharts, video, etc.)
          to avoid console warnings / wasted renders. */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-slate-50 to-[#EFF6FF] rounded-3xl" />
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-[#2563EB]/20 shadow-floating max-w-md w-full p-6 text-center space-y-3">
          <div className="w-14 h-14 bg-[#EFF6FF] rounded-2xl mx-auto flex items-center justify-center">
            {expired ? <Crown className="w-7 h-7 text-amber-500" /> : <Lock className="w-7 h-7 text-[#2563EB]" />}
          </div>
          <h3 className="font-heading text-xl font-bold">
            {expired ? "Subscription expired" : `Upgrade to unlock ${moduleLabel}`}
          </h3>
          <p className="text-sm text-slate-500">
            {description || (expired
              ? "Your records are safe — but advanced modules are paused. Renew your plan to restore full access."
              : "This module is part of a higher plan. Upgrade to keep your school running at full speed.")}
          </p>
          {isOwner ? (
            <Link to="/billing">
              <Button className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] mt-1" data-testid={`module-gate-upgrade-${moduleKey}`}>
                {expired ? "Renew now" : "Upgrade plan"} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          ) : (
            <p className="text-xs text-slate-400 italic">Please contact your school owner to renew or upgrade the plan.</p>
          )}
        </div>
      </div>
    </div>
  );
}
